'use es6';

import * as ObjectTypes from 'customer-data-objects/constants/ObjectTypes';
import * as OperatorTypes from './FilterContactSearchOperatorTypes';
import * as Operators from '../../filterQueryFormat/operator/Operators';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { BACKWARD, FORWARD, isRollingDateDirection } from 'customer-data-filters/filterQueryFormat/rollingDates/RollingDateDirections';
import { ESCAPED_RESERVE_WORDS, RESERVE_WORDS, FQF_TO_CONTACT_SEARCH_DIRECTION, CONTACT_SEARCH_TO_FQF_DIRECTION, FORWARD as CONTACT_SEARCH_DIRECTION_FORWARD, BACKWARD as CONTACT_SEARCH_DIRECTION_BACKWARD } from './constants';
import { List, Map as ImmutableMap, Seq } from 'immutable';
import And from '../../filterQueryFormat/logic/And';
import MissingField from '../../filterQueryFormat/MissingField';
import RollingDateConfig from 'customer-data-filters/filterQueryFormat/rollingDates/RollingDateConfig';
import curry from 'transmute/curry';
import getIn from 'transmute/getIn';
import identity from 'transmute/identity';
import invariant from 'react-utils/invariant';
import isString from 'transmute/isString';
import partial from 'transmute/partial';
import protocol from 'transmute/protocol';
import { resolveIsUngated } from '../../utilities/UserInfoHelper';
import { hasXDaysWithExclusiveBounds } from '../../permissions/gates';
export const TypeToOperator = ImmutableMap({
  [OperatorTypes.EQUAL]: Operators.Equal,
  [OperatorTypes.GREATER_OR_EQUAL]: Operators.GreaterOrEqual,
  [OperatorTypes.GREATER]: Operators.Greater,
  [OperatorTypes.GREATER_THAN_X_DAYS]: Operators.GreaterRolling,
  [OperatorTypes.IN_RANGE_ROLLING]: Operators.InRollingDateRange,
  [OperatorTypes.IN_RANGE]: Operators.InRange,
  [OperatorTypes.IN]: Operators.In,
  [OperatorTypes.KNOWN]: Operators.Known,
  [OperatorTypes.LESS_OR_EQUAL]: Operators.LessOrEqual,
  [OperatorTypes.LESS]: Operators.Less,
  [OperatorTypes.LESS_THAN_X_DAYS]: Operators.LessRolling,
  [OperatorTypes.NEAR]: Operators.Near,
  [OperatorTypes.NOT_EQUAL]: Operators.NotEqual,
  [OperatorTypes.NOT_IN]: Operators.NotIn,
  [OperatorTypes.NOT_KNOWN]: Operators.NotKnown,
  [OperatorTypes.TIME_UNIT_TO_DATE]: Operators.InRollingDateRange
});
const getInclusiveForXDays = (type, direction, isUngated) => {
  const useDefaultInclusive = !hasXDaysWithExclusiveBounds(isUngated);
  if (useDefaultInclusive) {
    return {
      lowerBoundInclusive: true,
      upperBoundInclusive: true
    };
  }
  if (type === OperatorTypes.GREATER_THAN_X_DAYS && direction === FORWARD) {
    return {
      lowerBoundInclusive: false,
      upperBoundInclusive: true // ignored, is unbounded in this direction
    };
  }
  if (type === OperatorTypes.GREATER_THAN_X_DAYS && direction === BACKWARD) {
    return {
      lowerBoundInclusive: true,
      // ignored, is unbounded in this direction
      upperBoundInclusive: false
    };
  }
  return {
    lowerBoundInclusive: true,
    upperBoundInclusive: true
  };
};
const defaultConvertTo = curry((type, operator) => {
  const result = {
    operator: type,
    property: operator.field.name
  };
  if (operator.has('highValue')) {
    result.highValue = operator.highValue;
  }
  if (operator.has('value')) {
    result.value = operator.value;
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.WildCardEqual, Operators.NotWildCardEqual].includes(operator.constructor)) {
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.EqualAny, Operators.NotEqualAny].includes(operator.constructor)) {
    result.propertySuffix = 'raw';
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim().toLowerCase();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.BOOLEAN && operator.has('value')) {
    result.value = operator.value ? 'true' : 'false';
  }
  if (![Operators.Known, Operators.NotKnown].includes(operator.constructor) && [PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(operator.field.type)) {
    result.dateTimeFormat = 'DATE';
  }
  return result;
});
const convertToIn = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  values: operator.value.toArray()
}));
const convertToNow = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  value: '__hs__NOW'
}));
const convertToXDaysWithDirection = curry((type, operator, getIsUngated) => {
  const safeIsUngated = resolveIsUngated(getIsUngated);
  return Object.assign({
    operator: type,
    property: operator.field.name,
    value: operator.numberOfDays,
    direction: FQF_TO_CONTACT_SEARCH_DIRECTION[operator.direction]
  }, getInclusiveForXDays(type, operator.direction, safeIsUngated));
});
const convertToInRangeRolling = curry((type, operator) => {
  const {
    direction,
    includeFutureDates,
    useFiscalYear,
    isInclusive,
    timeUnit,
    value
  } = operator.value;
  const operatorType = includeFutureDates ? type : OperatorTypes.TIME_UNIT_TO_DATE;
  const filter = {
    operator: operatorType,
    property: operator.field.name,
    inclusive: isInclusive,
    rollForward: direction === FORWARD,
    timeUnit,
    timeUnitCount: value
  };
  if (operator.rollingOffset) {
    filter.rollingOffset = operator.rollingOffset;
  }
  if (useFiscalYear) {
    filter.rollingOffset = '__hs__FISCAL_YEAR_OFFSET';
    filter.rollingOffsetUnit = 'MONTH';
  }
  return filter;
});
const convertToNear = curry((type, operator) => ({
  distanceUnit: 'MILES',
  distanceUnitCount: 25,
  operator: type,
  property: operator.field.name,
  value: operator.value
}));
const invalidOperator = operator => {
  invariant(false, 'cannot convert filter operator "%s" to ContactSearch filter', operator.name);
};
export const convertToContactSearch = protocol({
  name: 'convertToContactSearch',
  args: [protocol.TYPE, isString],
  fallback: invalidOperator
});
convertToContactSearch.implement(Operators.Equal, defaultConvertTo(OperatorTypes.EQUAL));
convertToContactSearch.implement(Operators.GreaterOrEqual, defaultConvertTo(OperatorTypes.GREATER_OR_EQUAL));
convertToContactSearch.implement(Operators.Greater, defaultConvertTo(OperatorTypes.GREATER));
convertToContactSearch.implement(Operators.GreaterRolling, convertToXDaysWithDirection(OperatorTypes.GREATER_THAN_X_DAYS));
convertToContactSearch.implement(Operators.InRange, defaultConvertTo(OperatorTypes.IN_RANGE));
convertToContactSearch.implement(Operators.In, convertToIn(OperatorTypes.IN));
convertToContactSearch.implement(Operators.InRollingDateRange, convertToInRangeRolling(OperatorTypes.IN_RANGE_ROLLING));
convertToContactSearch.implement(Operators.Known, defaultConvertTo(OperatorTypes.KNOWN));
convertToContactSearch.implement(Operators.Less, defaultConvertTo(OperatorTypes.LESS));
convertToContactSearch.implement(Operators.LessOrEqual, defaultConvertTo(OperatorTypes.LESS_OR_EQUAL));
convertToContactSearch.implement(Operators.LessRolling, convertToXDaysWithDirection(OperatorTypes.LESS_THAN_X_DAYS));
convertToContactSearch.implement(Operators.Near, convertToNear(OperatorTypes.NEAR));
convertToContactSearch.implement(Operators.NotEqual, defaultConvertTo(OperatorTypes.NOT_EQUAL));
convertToContactSearch.implement(Operators.NotIn, convertToIn(OperatorTypes.NOT_IN));
convertToContactSearch.implement(Operators.NotKnown, defaultConvertTo(OperatorTypes.NOT_KNOWN));
convertToContactSearch.implement(Operators.WildCardEqual, defaultConvertTo(OperatorTypes.EQUAL));
convertToContactSearch.implement(Operators.NotWildCardEqual, defaultConvertTo(OperatorTypes.NOT_EQUAL));
convertToContactSearch.implement(Operators.BeforeNow, convertToNow(OperatorTypes.LESS_OR_EQUAL));
convertToContactSearch.implement(Operators.EqualAny, defaultConvertTo(OperatorTypes.EQUAL));
convertToContactSearch.implement(Operators.NotEqualAny, defaultConvertTo(OperatorTypes.NOT_EQUAL));

/**
 * `null` as a value can cause a whole bunch of issues so
 * lets always convert it over to a string.
 */
function convertNullToString(value) {
  return value === null ? 'null' : value;
}
function convertStringToBool(value) {
  return typeof value === 'string' ? value === 'true' : value;
}

// We don't fully support contact search, we base our implementation on CRM search
// In most cases this will work, but Contact Search is not recommended to be used, please use CRM Search instead
// @deprecated Please use toCRMSearch
export function toContactSearch(filter, getIsUngated) {
  return toCRMSearch(filter, getIsUngated);
}
export function toCRMSearch(filter, getIsUngated) {
  const validIsUngated = resolveIsUngated(getIsUngated);
  return filter.conditions.reduce((acc, op) => {
    acc.push(convertToContactSearch(op, validIsUngated));
    return acc;
  }, []);
}
function defaultConvertFrom(Operator, field, entry) {
  const {
    value,
    propertySuffix
  } = entry;
  const safeValue = field.type === PropertyTypes.BOOLEAN ? convertStringToBool(value) : convertNullToString(value);
  if (field.type === PropertyTypes.STRING && [Operators.Equal, Operators.NotEqual].includes(Operator)) {
    const valuesArray = typeof value === 'string' ? value.split(' OR ') : [`${value}`];
    const values = value ? List(valuesArray.map(convertNullToString)).map(v => {
      return ESCAPED_RESERVE_WORDS.includes(v) ? v.replace(/"/g, '') : v;
    }) : List();
    if (propertySuffix === 'raw') {
      const RealOperator = Operator === Operators.Equal ? Operators.EqualAny : Operators.NotEqualAny;
      return RealOperator.of(field, values);
    }
    const RealOperator = Operator === Operators.Equal ? Operators.WildCardEqual : Operators.NotWildCardEqual;
    return RealOperator.ofUnsafe(field, values);
  }
  if ([PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(field.type) && [Operators.LessOrEqual].includes(Operator)) {
    if (value === '__hs__NOW') {
      return Operators.BeforeNow.ofUnsafe(field, safeValue);
    }
  }
  return Operator.ofUnsafe(field, safeValue);
}

/**
 * A while back I messed up and allowed listMemberships to have multiple values
 * in an Equal/NotEqual operator which just doesn't work. This converts to an
 * IN/NOT_IN which actually works. -Colby
 */
function convertWithOverride(OverrideOperator, Operator, field, operator) {
  const {
    property,
    value
  } = operator;
  switch (property) {
    case 'listMemberships.listId':
    case 'ilsListMemberships.listId':
      return OverrideOperator.of(field, List(Array.isArray(value) ? value : value.split(',')));
    default:
      return defaultConvertFrom(Operator, field, operator);
  }
}
function convertFromIn(Operator, field, entry) {
  const {
    values
  } = entry;
  const operatorValues = values ? List(values.map(convertNullToString)) : List();
  return Operator.of(field, operatorValues);
}

// When convertFromXDaysWithDirection is called, we might need to adjust the
// value the to match the expected inclusive flags set in getInclusiveForXDays
const maybeAdjustForInclusive = (value, Operator, entry, isUngated) => {
  const requiresInclusiveAdjustment = hasXDaysWithExclusiveBounds(isUngated) && Operator === Operators.GreaterRolling;
  if (requiresInclusiveAdjustment) {
    const forwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_FORWARD && entry.lowerBoundInclusive !== false;
    const backwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_BACKWARD && entry.upperBoundInclusive !== false;
    if (forwardCase || backwardCase) {
      return value - 1;
    }
  }
  const requiresInclusiveInverseAdjustment = !hasXDaysWithExclusiveBounds(isUngated) && Operator === Operators.GreaterRolling;
  if (requiresInclusiveInverseAdjustment) {
    const forwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_FORWARD && entry.lowerBoundInclusive === false;
    const backwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_BACKWARD && entry.upperBoundInclusive === false;
    if (forwardCase || backwardCase) {
      return value + 1;
    }
  }
  return value;
};
const convertFromXDaysWithDirection = (Operator, field, entry, isUngated) => {
  const safeIsUngated = resolveIsUngated(isUngated);
  const value = +entry.value;
  // when direction is missing (for legacy filters)
  if (!entry.direction) {
    const numberOfDays = Math.abs(value);
    const direction = value <= 0 ? BACKWARD : FORWARD;
    return Operator.of(field, maybeAdjustForInclusive(numberOfDays, Operator, entry, safeIsUngated), direction);
  }

  // when direction is present (for legacy filters)
  if (isRollingDateDirection(entry.direction)) {
    return Operator.of(field, maybeAdjustForInclusive(value, Operator, entry, safeIsUngated), entry.direction);
  }
  // when direction is present
  const direction = CONTACT_SEARCH_TO_FQF_DIRECTION[entry.direction] || FORWARD;
  return Operator.of(field, maybeAdjustForInclusive(value, Operator, entry, safeIsUngated), direction);
};
function convertFromInRange(Operator, field, opts) {
  // Accept legacy field in addition to new one (#343)
  const {
    ['high_value']: highValueLegacy,
    highValue: highValueDefault,
    value
  } = opts;
  const highValue = typeof highValueDefault !== 'undefined' ? highValueDefault : highValueLegacy;
  return Operator.of(field, value, highValue);
}
function convertFromInRangeRolling(Operator, field, {
  inclusive = false,
  operator,
  rollForward = false,
  rollingOffset = 0,
  timeUnit,
  timeUnitCount
}) {
  const direction = rollForward === true ? FORWARD : BACKWARD;
  const includeFutureDates = operator === OperatorTypes.IN_RANGE_ROLLING;
  const useFiscalYear = rollingOffset === '__hs__FISCAL_YEAR_OFFSET';
  return Operator.of(field, RollingDateConfig({
    direction,
    includeFutureDates,
    useFiscalYear,
    isInclusive: inclusive,
    timeUnit,
    value: timeUnitCount
  }), useFiscalYear ? 0 : rollingOffset);
}
export const convertFromContactSearch = () => ImmutableMap({
  [OperatorTypes.EQUAL]: partial(convertWithOverride, Operators.In),
  [OperatorTypes.GREATER]: defaultConvertFrom,
  [OperatorTypes.GREATER_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.GREATER_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.IN]: convertFromIn,
  [OperatorTypes.IN_RANGE]: convertFromInRange,
  [OperatorTypes.IN_RANGE_ROLLING]: convertFromInRangeRolling,
  [OperatorTypes.KNOWN]: defaultConvertFrom,
  [OperatorTypes.LESS]: defaultConvertFrom,
  [OperatorTypes.LESS_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.LESS_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.NEAR]: defaultConvertFrom,
  [OperatorTypes.NOT_EQUAL]: partial(convertWithOverride, Operators.NotIn),
  [OperatorTypes.NOT_IN]: convertFromIn,
  [OperatorTypes.NOT_KNOWN]: defaultConvertFrom,
  [OperatorTypes.TIME_UNIT_TO_DATE]: convertFromInRangeRolling
});

// We don't fully support contact search, we base our implementation on CRM search
// In most cases this will work, but Contact Search is not recommended to be used, please use CRM Search instead
// @deprecated Please use fromCRMSearch
export function fromContactSearch(fields, json, defaultFilterFamily, getIsUngated) {
  return fromCRMSearch(fields, json, defaultFilterFamily, getIsUngated);
}
export function fromCRMSearch(fields, json, defaultFilterFamily = ObjectTypes.CONTACT, getIsUngated) {
  // HACK: shim for CRM-Issues#955 transition
  const getField = typeof fields === 'function' ? fields : keyPath => getIn(keyPath, fields);
  if (!fields || !json) {
    return And();
  }
  const validIsUngated = resolveIsUngated(getIsUngated);
  const convertEntry = (filterFamily, entry) => {
    const Operator = TypeToOperator.get(entry.operator);
    invariant(Operator !== undefined, 'contact search operator `%s` is not defined in OperatorConversion', entry.operator);
    const converter = convertFromContactSearch().get(entry.operator);
    invariant(typeof converter === 'function', 'no converter defined from `%s` to `%s` operator', entry.operator, Operator.toString());
    const fieldRecord = getField([filterFamily, entry.property]) || getField([entry.property]) || MissingField({
      name: entry.property,
      type: PropertyTypes[entry.dateTimeFormat] || PropertyTypes.STRING
    });
    return converter(Operator, fieldRecord, entry, validIsUngated);
  };
  const entries = Seq(json).map(entry => convertEntry(entry.filterFamily || defaultFilterFamily, entry));
  return And.of(...entries).set('filterFamily', defaultFilterFamily);
}